/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/no-unused-prop-types */
import {
  Checkbox, Col, Divider, Flex, message, Row
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import {
  Button, Image, Radio, Typography
} from 'tfc-components';

import ChoosePackage from './ChoosePackage';

import {
  IBusinessInfo, IForm,
  InvoiceFormType,
  TCleanRequire
} from '.';

import backupCardImg from 'assets/images/backup_card.jpg';
import imgCard from 'assets/images/card_notice.png';
import masterCard from 'assets/images/master_card.png';
import nganLuongImg from 'assets/images/nganluong-logo-color.png';
import mainCardImg from 'assets/images/primary_card.jpg';
import visa from 'assets/images/visa.png';
import Modal from 'components/organisms/Modal';
import { changePlanService, getCardService } from 'services/overview';
import { CardData, ChangePlanData } from 'services/overview/types';
import { ServicePackageAddOnData, ServicePackageData } from 'services/servicePackages/type';
import mapModifiers from 'utils/functions';

interface Props {
  total: number;
  duration: 'monthly' | 'yearly';
  billingType: 'one-time' | 'subscription';
  billingData: InvoiceFormType;
  visiblePackages: ServicePackageData[] | null;
  handleCheckout: () => void;
  mainPackageSelected: ServicePackageData | null;
  method: UseFormReturn<IForm>
  totalTiers: any;
  maxDuration: number;
  cleanRequire: TCleanRequire;
  typeStart: string
  addOnValues: ServicePackageAddOnData[];
  invoiceFormWatch: InvoiceFormType;
  dataDiscount?: {
    discountAmount: number,
    discountDay: number
  };
}

const paymentMethodsSubscription = [
  'JCB',
  'Master Card',
  'Visa'
];

const paymentMethodsOnetime = [
  'Viet QR',
  'ATM Card',
  'Internet Banking',
  'E-Wallet',
  'JCB',
  'Master Card',
  'Visa'
];

const RETURN_URL: string = `${window.location.origin}/change-plan/payment-status/success`;
const CANCEL_URL: string = `${window.location.origin}/change-plan/payment-status/cancel`;

const Confirmation: React.FC<Props> = ({
  total, duration, billingData, visiblePackages, billingType, handleCheckout,
  mainPackageSelected,
  method,
  typeStart, addOnValues, invoiceFormWatch, dataDiscount, cleanRequire
}) => {
  const [checked, setChecked] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [usedCard, setUsedCard] = useState<'main' | 'backup'>('main');
  const [paymentMethod, setMethod] = useState<'card' | 'other'>('card');
  const [mainCard, setMainCard] = useState<CardData | null>(null);
  const [backupCard, setBackupCard] = useState<CardData | null>(null);

  /* Queries */
  const { data: cardData, } = useQuery({
    queryKey: ['get-card'],
    queryFn: getCardService,
  });

  /* Mutation */
  const { mutateAsync: handleChangePlan } = useMutation({
    mutationKey: ['change-plan'],
    mutationFn: changePlanService,
    onError: (errors: ErrorResponse[]) => {
      if (errors.length > 0) {
        errors.forEach((error: ErrorResponse) => {
          message.error(error?.message || '');
        });
      } else {
        message.error('Something went wrong');
      }
    },
    onSuccess: (data) => {
      localStorage.setItem('paymentChecked', data.code);
      window.location.href = (data.link);
    }
  });

  useEffect(() => {
    const mainCardCandidate = cardData?.find(
      (card) => card?.cardInfoData?.cardIndex === 1
    );
    const backupCardCandidate = cardData?.find(
      (card) => card?.cardInfoData?.cardIndex === 2
    );

    if (mainCardCandidate) {
      setMainCard(mainCardCandidate);
      setBackupCard(backupCardCandidate ?? null);
    } else if (backupCardCandidate) {
      setMainCard(backupCardCandidate);
      setBackupCard(null);
    } else {
      setBackupCard(null);
      setMainCard(null);
    }
  }, [cardData, cardData?.length]);

  const onPayment = () => {
    const allChatbotCodes = Object.values(cleanRequire.chatbot.code).flat();
    const allWebCodes = Object.values(cleanRequire.web.code).flat();
    const allFileCodes = Object.values(cleanRequire.file.code).flat();
    const body: ChangePlanData = {
      switchType: typeStart === 'now' ? 1 : 2,
      packageData: {
        billingType: billingType === 'one-time' ? 1 : 2,
        servicePackageId: mainPackageSelected?.id || 1,
        quantity: mainPackageSelected?.quantity || 1,
        servicePackageAddOns: addOnValues.map((item) => ({
          servicePackageAddOnId: item?.packageId || 1,
          quantity: item?.quantity || 1,
          unit: item?.unit || 1
        }))
      },
      invoiceData: {
        invoiceType: invoiceFormWatch.invoiceType,
        taxCode: (invoiceFormWatch.billingInfo as IBusinessInfo).taxId,
        companyName: (invoiceFormWatch.billingInfo as IBusinessInfo).companyName,
        fullAddress: (invoiceFormWatch.billingInfo as IBusinessInfo).fullAddress,
        invoiceEmails: invoiceFormWatch.billingInfo?.invoiceEmail?.filter((item) => item) || []
      },
      paymentMethod: 1,
      returnUrl: RETURN_URL,
      cancelUrl: CANCEL_URL,
      cardTokenId: (() => {
        if (paymentMethod !== 'card') return null;
        if (usedCard === 'main') return mainCard?.cardInfoData?.id || 0;
        return backupCard?.cardInfoData?.id || 0;
      })(),

      reqDelData: {
        agentCodes: allChatbotCodes,
        documentCodes: [...allWebCodes, ...allFileCodes]
      }
    };
    handleChangePlan(body);
  };

  useEffect(() => {
    if (cardData?.length === 0) {
      setMethod('other');
    }
  }, [cardData]);

  const totalFileCleaned = useMemo(() => Object.values(cleanRequire.file.cleaned)
    .reduce((sum, value) => sum + value, 0), [cleanRequire.file.cleaned]);

  const totalWebCleaned = useMemo(() => Object.values(cleanRequire.web.cleaned)
    .reduce((sum, value) => sum + value, 0), [cleanRequire.web.cleaned]);

  const totalStorageCleaned = useMemo(() => Object.values(cleanRequire.storage.cleaned)
    .reduce((sum, value) => sum + value, 0), [cleanRequire.storage.cleaned]);

  const totalChatbotCleaned = useMemo(() => Object.values(cleanRequire.chatbot.cleaned)
    .reduce((sum, value) => sum + value, 0), [cleanRequire.chatbot.cleaned]);
  return (
    <div className="p-checkout_confirmation">
      <div className="p-checkout_confirmation_wrap">
        <ChoosePackage
          billingType={billingType}
          total={total}
          duration={duration}
          readonly
          visiblePackages={visiblePackages}
          width="158px"
          mainPackageSelected={mainPackageSelected}
          method={method}
          isConfirm
          dataDiscount={dataDiscount}
        />

        {/* <div className="p-paymentMethod_packageDetail">
          <Typography.Text fontweight="600" extendClasses="fs-20x24">Plan Overview</Typography.Text>

          <div className="p-paymentMethod_packageDetail_description">
            <Typography.Text>
              By renewing this package, you will continue to
              use the same resources without interruption, starting from
              {' '}
              {dayjs().format('DD/MM/YYYY')}
              {' '}
              until
              {' '}
              {dayjs().add(maxDuration, duration ===
              'monthly' ? 'months' : 'years').format('DD/MM/YYYY')}
            </Typography.Text>

            {totalTiers.numberOfAgent > 0 && (
              <Typography.Text>
                Number of chatbots:
                {' '}
                {totalTiers.numberOfAgent}
                {' '}
                chatbots
              </Typography.Text>
            )}

            {totalTiers.numberOfSession > 0 && (
              <Typography.Text>
                Number of sessions:
                {' '}
                {totalTiers.numberOfSession}
                {' '}
                chatbot sessions
              </Typography.Text>
            )}

            {totalTiers.numberOfFile > 0 && (
              <Typography.Text>{`Knowledge base capacity: ${totalTiers.numberOfFile}
              files${totalTiers.numberOfWebsite > 0 && `, ${totalTiers.numberOfWebsite}`}
              websites${totalTiers.totalSizeLimit > 0 && `,
               ${totalTiers.totalSizeLimit} MB storage`},
               and permission to access to all features included in the plan`}</Typography.Text>
            )}
          </div>
        </div> */}

        <div className="p-checkout_spacingBottom p-changePlan_cleanData_mt32">
          <Typography.Text fontweight="600" extendClasses="p-checkout_titleProduct p-checkout_billingInfo_spacing fs-20x24">
            Invoice Information
          </Typography.Text>
          <div className="p-checkout_buyerInfo">
            <Row>
              <Col className="p-checkout_buyerInfo_col">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">Invoice Recipient Type</Typography.Text>
                <Typography.Text extendClasses="p-checkout_text">
                  {billingData.invoiceType === 2 ? 'Business' : 'Individual'}
                </Typography.Text>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col className="p-checkout_buyerInfo_col">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  {billingData.invoiceType === 2
                    ? 'Tax ID' : 'National ID'}
                </Typography.Text>
                <Typography.Text extendClasses="p-checkout_text">
                  {billingData.invoiceType === 2 ? (billingData?.billingInfo as IBusinessInfo).taxId
                    : (billingData?.billingInfo as IBusinessInfo).taxId}
                </Typography.Text>
              </Col>
              <Col style={{ flex: 1 }} className="p-checkout_buyerInfo_col">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  {billingData.invoiceType === 2
                    ? 'Company name' : 'Full name'}
                </Typography.Text>
                <Typography.Text extendClasses="p-checkout_text">
                  {billingData.invoiceType === 2
                    ? (billingData.billingInfo as IBusinessInfo).companyName
                    : (billingData.billingInfo as IBusinessInfo).companyName}
                </Typography.Text>
              </Col>
            </Row>
            <Col className="">
              <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">Full Address</Typography.Text>
              <Typography.Text extendClasses="p-checkout_text">{billingData.billingInfo.fullAddress}</Typography.Text>
            </Col>
            <Row style={{ marginTop: 32 }}>
              <Col>
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">Invoice Email</Typography.Text>
                <div className="p-checkout_textTagWrap">
                  {billingData.billingInfo.invoiceEmails?.map((item) => (
                    <Typography.Text extendClasses="p-checkout_textTag">{item}</Typography.Text>
                  ))}
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {
          (cleanRequire.chatbot.needToClean > 0
            || cleanRequire.file.needToClean > 0
            || cleanRequire.storage.needToClean > 0 || cleanRequire.web.needToClean > 0) && typeStart === 'now'
          && (
            <div>
              <Typography.Text extendClasses="fs-20x24" fontweight="600">Data cleanup</Typography.Text>
              <Flex className="p-changePlan_dataCleanWrap">
                <Flex gap={8} flex={1} vertical>
                  <Typography.Text extendClasses="p-changePlan_dataCleanTitle fs-14x20">Number of chatbot</Typography.Text>
                  <Typography.Text extendClasses="fs-28x33 p-changePlan_dataCleanValue" fontweight="600">{totalChatbotCleaned}</Typography.Text>
                  <Typography.Text extendClasses="fs-12x16 p-changePlan_dataCleanTitle ">Cleaned</Typography.Text>
                </Flex>

                <Flex gap={8} flex={1} vertical>
                  <Typography.Text extendClasses="p-changePlan_dataCleanTitle fs-14x20">Storage (MB)</Typography.Text>
                  <Typography.Text extendClasses="fs-28x33 p-changePlan_dataCleanValue" fontweight="600">{totalStorageCleaned.toFixed(2)}</Typography.Text>
                  <Typography.Text extendClasses="fs-12x16 p-changePlan_dataCleanTitle ">Cleaned</Typography.Text>
                </Flex>

                <Flex gap={8} flex={1} vertical>
                  <Typography.Text extendClasses="p-changePlan_dataCleanTitle fs-14x20">Number of files </Typography.Text>
                  <Typography.Text extendClasses="fs-28x33 p-changePlan_dataCleanValue" fontweight="600">{totalFileCleaned}</Typography.Text>
                  <Typography.Text extendClasses="fs-12x16 p-changePlan_dataCleanTitle ">Cleaned</Typography.Text>
                </Flex>

                <Flex gap={8} flex={1} vertical>
                  <Typography.Text extendClasses="p-changePlan_dataCleanTitle fs-14x20">Number of web</Typography.Text>
                  <Typography.Text extendClasses="fs-28x33 p-changePlan_dataCleanValue" fontweight="600">{totalWebCleaned}</Typography.Text>
                  <Typography.Text extendClasses="fs-12x16 p-changePlan_dataCleanTitle ">Cleaned</Typography.Text>
                </Flex>
              </Flex>
            </div>
          )
        }

      </div>
      <div className="p-addNewPackage_confirmation_payment p-addNewPackage_productWrap">
        <div>
          <div className="p-addNewPackage_padding24">
            <Typography.Text fontweight="600" extendClasses=" p-home_demo_formLabel fs-20x24">{cardData && cardData.length > 0 ? 'Payment method' : 'Support payment method'}</Typography.Text>
          </div>

          <div className="p-addNewPackage_confirmation_paymentMethods p-addNewPackage_padding24">
            {
              mainCard
              && (
                <div>
                  <Flex gap={4} align="center">
                    <Radio checked={paymentMethod === 'card'} onClick={() => setMethod('card')} size={20} />
                    <Typography.Text fontweight="600" extendClasses="fs-14x20">Your card</Typography.Text>
                  </Flex>
                  {
                    paymentMethod === 'card'
                    && (
                      <>
                        <div className="t-payment_card_imgCardWrap">
                          <Image
                            imgSrc={usedCard === 'main' ? mainCardImg : backupCardImg}
                            alt="primary-card"
                            ratio={310 / 171}
                            extendClasses="p-paymentMethod_cardInfo_cardImg"
                          />
                          {usedCard === 'main'

                            ? (
                              <div className="t-overview_cardInfo  t-overview_cardInfo-padding16">
                                <Image
                                  imgSrc={
                                    mainCard?.cardInfoData?.paymentMethod === 'MASTERCARD'
                                      ? masterCard
                                      : visa
                                  }
                                  ratio={50 / 32}
                                  alt="card-brand"
                                  extendClasses="t-overview_cardInfo_cardBrannd"
                                />
                                <Typography.Text
                                  extendClasses="fs-28x33 t-overview_cardInfo_cardNumber"
                                  fontweight="600"
                                >
                                  {mainCard?.cardInfoData?.cardNumber}
                                </Typography.Text>

                                <Flex justify="space-between">
                                  <Flex vertical>
                                    <Typography.Text>Card holder</Typography.Text>
                                    <Typography.Text
                                      extendClasses="fs-16x24 t-overview_cardInfo_cardNumber"
                                      fontweight="600"
                                    >
                                      {mainCard?.cardInfoData?.cardHolderName}
                                    </Typography.Text>
                                  </Flex>
                                  <Flex vertical align="flex-end">
                                    <Typography.Text>Expires </Typography.Text>
                                    <Typography.Text
                                      extendClasses="fs-16x24 t-overview_cardInfo_cardNumber"
                                      fontweight="600"
                                    >
                                      {`${mainCard?.cardInfoData?.cardExpireMonth}/${mainCard?.cardInfoData?.cardExpireYear}`}
                                    </Typography.Text>
                                  </Flex>
                                </Flex>
                              </div>
                            )
                            : (
                              <div className="t-overview_cardInfo  t-overview_cardInfo-padding16">
                                <Image
                                  imgSrc={
                                    backupCard?.cardInfoData?.paymentMethod === 'MASTERCARD'
                                      ? masterCard
                                      : visa
                                  }
                                  ratio={50 / 32}
                                  alt="card-brand"
                                  extendClasses="t-overview_cardInfo_cardBrannd"
                                />
                                <Typography.Text
                                  extendClasses="fs-28x33 t-overview_cardInfo_cardNumber"
                                  fontweight="600"
                                >
                                  {backupCard?.cardInfoData?.cardNumber}
                                </Typography.Text>

                                <Flex justify="space-between">
                                  <Flex vertical>
                                    <Typography.Text>Card holder</Typography.Text>
                                    <Typography.Text
                                      extendClasses="fs-16x24 t-overview_cardInfo_cardNumber"
                                      fontweight="600"
                                    >
                                      {backupCard?.cardInfoData?.cardHolderName}
                                    </Typography.Text>
                                  </Flex>
                                  <Flex vertical align="flex-end">
                                    <Typography.Text>Expires </Typography.Text>
                                    <Typography.Text
                                      extendClasses="fs-16x24 t-overview_cardInfo_cardNumber"
                                      fontweight="600"
                                    >
                                      {`${backupCard?.cardInfoData?.cardExpireMonth}/${backupCard?.cardInfoData?.cardExpireYear}`}
                                    </Typography.Text>
                                  </Flex>
                                </Flex>
                              </div>
                            )}

                        </div>

                        {backupCard && (
                          <Button
                            onClick={() => {
                              if (usedCard === 'main') {
                                setUsedCard('backup');
                              } else {
                                setUsedCard('main');
                              }
                            }}
                            extendClasses="p-addNewPackage_cardInfo_btnTxt"
                            variant="text"
                          >
                            {usedCard === 'main' ? 'Use backup card' : 'Use primary card'}
                          </Button>
                        )}

                      </>
                    )
                  }
                </div>
              )
            }

            <div className={`p-paymentMethod_cardInfo_others ${cardData && cardData.length === 0 ? 'p-addNewPackage_mt0' : ''}`}>
              {
                cardData && cardData?.length > 0
                && (
                  <Flex gap={4} align="center">
                    <Radio checked={paymentMethod === 'other'} onClick={() => setMethod('other')} size={20} />
                    <Typography.Text fontweight="600" extendClasses="fs-14x20">Others</Typography.Text>
                  </Flex>
                )
              }

              {
                paymentMethod === 'other' && (
                  <ul className="p-addNewPackage_confirmation_listPayment">
                    {(billingType === 'one-time' ? paymentMethodsOnetime : paymentMethodsSubscription)
                      .map((item) => (
                        <li
                          className="p-addNewPackage_confirmation_radio"
                          key={item}
                        >
                          <Typography.Text extendClasses="p-addNewPackage_text fs-14x18">{item}</Typography.Text>
                        </li>
                      ))}
                  </ul>
                )
              }

            </div>
          </div>

          <div className="p-addNewPackage_padding24">
            <div>
              <Row justify="space-between">
                <Typography.Text extendClasses="fs-16x18">Total</Typography.Text>
                <Typography.Text fontweight="600" extendClasses="p-addNewPackage_packageCard_amount fs-18x21">
                  {total.toLocaleString('vi')}
                  {' '}
                  VNĐ
                </Typography.Text>
              </Row>
              <div className="p-addNewPackage_confirmation_agreement">
                <Checkbox onChange={() => setChecked(!checked)} />
                <Typography.Text extendClasses="p-addNewPackage_confirmation_agreementText fs-14x18">
                  I agree to Ask2Auto
                  {' '}
                  <span className="p-addNewPackage_confirmation_link" onClick={() => window.open('/privacy-policy', '_blank')}>privacy policy</span>
                  {' '}
                  and
                  {' '}
                  <span className="p-addNewPackage_confirmation_link" onClick={() => window.open('/terms-and-conditions', '_blank')}>terms</span>
                </Typography.Text>
              </div>
              <Button
                onClick={onPayment}
                disabled={!checked}
                extendClasses={mapModifiers('fButton fButton-primary p-addNewPackage_confirmation_button', !checked ? 'disabled' : 'active')}
              >
                {paymentMethod === 'card' ? 'Pay' : 'Select Payment Method'}
              </Button>
            </div>
            <div className="p-addNewPackage_confirmation_support">
              <Typography.Text extendClasses="p-addNewPackage_confirmation_supportText">Supported by payment gateway</Typography.Text>
              <div className="p-addNewPackage_confirmation_supportImg">
                <Image extendClasses="p-addNewPackage_confirmation_extend" imgSrc={nganLuongImg} alt="nganLuongImg" ratio={394 / 87} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={openModal}
        className="p-checkout_modal"
        width={800}
        title="Notice"
        closeIcon
        cancelButton={{
          handleClick: () => {
            setOpenModal(false);
          },
        }}
        okButton={{
          handleClick: () => {
            setOpenModal(false);
          },
        }}
      >
        <div className="p-checkout_modal_content">
          <Divider style={{ margin: '8px 0 16px 0' }} />
          <Image extendClasses="p-checkout_modal_image" imgSrc={imgCard} alt="demoImg" ratio={1447 / 680} />
          <Typography.Text extendClasses="p-checkout_modal_description f-14x18">
            To enable automatic billing, you must agree to save
            your card information with our payment partner
          </Typography.Text>
          <Divider style={{ margin: '12px 0' }} />
          <div className="p-checkout_modal_buttonWrap">
            <Button onClick={() => handleCheckout()} variant="primary" extendClasses="p-checkout_modal_button">I understand</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Confirmation;
